// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ChipProps } from '@mui/material';
import {
  Check,
  Clock,
  Download,
  Edit,
  Eye,
  FileText,
  MoreHorizontal,
  Paperclip,
  Plus,
  Receipt,
  Repeat,
  Share2,
  X as CloseIcon,
} from 'lucide-react';
import api from '@crm-src/lib/api';
import { formatCurrency as defaultFormatCurrency } from '@crm-src/lib/utils';
import { formatDate } from '@crm-src/lib/date-utils';
import { downloadDocumentPdf } from '@/lib/documentPdf';
import { shareDocumentPdfFilename } from '@crm-src/lib/shareDocumentDefaults';
import { invoicesApi } from '@crm-src/lib/invoices-api';
import type { Invoice, InvoicePagination } from '@crm-src/lib/store/invoicesSlice';
import { toast } from '@/components/ui/use-toast';
import { Pagination } from '@/components/ui/pagination';
import { InvoiceStatusBadge } from '@crm-src/pages/invoices/invoice-status-badge';
import { DeleteInvoiceModal } from './delete-invoice-modal';
import { MarkAsPaidModal } from './mark-as-paid-modal';
import { ShareDocumentModal } from './share-document-modal';
import { SearchInput } from './search-input';
import { SortableHeader } from './table/SortableHeader';
import { buildTableParams, nextSortState, type SortState } from './table/table-query';

/**
 * The `/invoices` serializer returns `has_file` + `original_filename` (the
 * attachment flags) which the shared `Invoice` type doesn't declare — extend it
 * locally rather than widen the shared slice type.
 */
interface InvoiceRow extends Invoice {
  has_file?: boolean;
  original_filename?: string;
}

interface CustomerInvoicesTableProps {
  /** The customer whose documents this table lists (server-side scoped). */
  customerId: string;
  documentType: 'invoice' | 'quote';
  /** IDs of invoices that carry an active/paused recurring schedule. */
  recurringInvoiceIds?: string[];
  /** Called after any mutation so the parent can refresh agreement counts. */
  onChanged?: () => void;
  formatCurrency?: (amount: number) => string;
  className?: string;
}

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

function parseAmount(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value) || 0;
  return 0;
}

/** Quote lifecycle status → chip color + label (invoices use InvoiceStatusBadge). */
function quoteStatusChip(status?: string): { color: ChipProps['color']; label: string } {
  switch (status) {
    case 'expired':
      return { color: 'error', label: 'Expired' };
    case 'converted':
      return { color: 'success', label: 'Converted' };
    case 'rejected':
      return { color: 'default', label: 'Rejected' };
    case 'pending':
    default:
      return { color: 'warning', label: 'Pending' };
  }
}

/**
 * The customer Billing tab's Invoices / Quotes table.
 *
 * Modern, self-contained MUI table that paginates AND sorts on the SERVER
 * (`GET /api/v1/invoices?customer_id=…&document_type=…&page&per_page&sort&dir&
 * search`) — the previous card-list rendered every invoice at once and
 * paginated/searched in the browser. Sorting rides the crm-api allow-list
 * (see components/table/). Rows carry the full action set (view /
 * edit / share / download / mark paid / delete) with the correct `/crm`-mounted
 * navigation.
 *
 * Financial-only: it renders money via `formatCurrency` and is mounted inside a
 * `<FinancialOnly>` gate by DealsInvoicesPanel — see financial-wall.test.ts.
 */
export function CustomerInvoicesTable({
  customerId,
  documentType,
  recurringInvoiceIds = [],
  onChanged,
  formatCurrency = defaultFormatCurrency,
  className = '',
}: CustomerInvoicesTableProps) {
  const navigate = useNavigate();
  const isQuote = documentType === 'quote';
  const noun = isQuote ? 'Quote' : 'Invoice';
  const Icon = isQuote ? FileText : Receipt;

  // Server-driven list state.
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [pagination, setPagination] = useState<InvoicePagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Query controls (each change re-fetches).
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  // Sort lives in component state, NOT in the URL via `useTableQuery`: the
  // Billing tab mounts TWO of these tables at once (invoices + quotes, see
  // deals-invoices-panel.tsx), so a single shared `?sort=` would make one
  // table's header click reorder the other. The pure helpers below are the
  // shared ones all the same — only the storage is local.
  const [sortState, setSortState] = useState<SortState>({ sort: 'issue_date', dir: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');

  // Row-action menu + modals.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [shareDoc, setShareDoc] = useState<InvoiceRow | null>(null);
  const [paidDoc, setPaidDoc] = useState<InvoiceRow | null>(null);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  // Guards stale responses from a slower earlier request clobbering a newer one.
  const requestSeq = useRef(0);
  // Bumped to force a reload after a mutation (delete / mark paid / …).
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!customerId) return;
    const seq = ++requestSeq.current;
    setIsLoading(true);
    setError(null);

    // `customer_id` + `document_type` are plain request params the crm-api
    // handles bespokely (they are NOT declared facets), so they ride through
    // `buildTableParams`' extra-params bag — which comma-joins each value and
    // drops an empty one, exactly what a single-valued param wants. Everything
    // else — page / per_page / sort / dir / a trimmed non-blank search — is the
    // shared builder's own job.
    const params = buildTableParams(
      { sort: sortState.sort, dir: sortState.dir },
      { customer_id: [customerId], document_type: [documentType] },
      page,
      perPage,
      searchQuery,
    );

    api
      .get('/invoices', { params })
      .then((response) => {
        if (seq !== requestSeq.current) return; // a newer request already won
        const data: InvoiceRow[] = response.data?.data ?? response.data ?? [];
        const meta: InvoicePagination | null = response.data?.meta?.pagination ?? null;
        setRows(Array.isArray(data) ? data : []);
        setPagination(meta);
        setIsLoading(false);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setRows([]);
        setPagination(null);
        setError(err?.response?.data?.error || `Failed to load ${noun.toLowerCase()}s`);
        setIsLoading(false);
      });
  }, [customerId, documentType, page, perPage, sortState.sort, sortState.dir, searchQuery, reloadToken, noun]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  // Every column this table offers a header for is server-declared (see
  // `components/table/server-keys.json` → invoices.sortable), so there is no
  // runtime allow-list to re-check here: an undeclared key could only come
  // from editing the header row below, and the server ignores one anyway.
  const handleSort = useCallback((column: string) => {
    setSortState((current) => nextSortState(current, column));
    setPage(1);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setPage(1);
  }, []);

  // --- Navigation (all `/crm`-prefixed: the CRM routes are mounted under /crm,
  // so a bare `/invoices/…` misses the mount and dead-ends on /dashboard). ---
  const basePath = isQuote ? '/crm/quotes' : '/crm/invoices';
  const goView = (doc: Invoice) => navigate(`${basePath}/${doc.id}`);
  const goEdit = (doc: Invoice) => navigate(`${basePath}/generate?invoiceId=${doc.id}&customerId=${customerId}`);
  const goNew = () =>
    navigate(customerId ? `${basePath}/generate?customerId=${customerId}` : `${basePath}/generate`);

  // --- Mutations (self-contained: hit crm-api, toast, reload this list + notify
  // the parent so the Agreement card's counts refresh). ---
  const handleConfirmDelete = async (id: string) => {
    const res = await invoicesApi.delete(id);
    setDeleteId(null);
    if (res.success) {
      toast({ title: `${noun} Deleted`, description: `${noun} has been successfully deleted.` });
      reload();
      onChanged?.();
    } else {
      toast({
        title: 'Delete Failed',
        description: res.errors?.[0] || `Failed to delete ${noun.toLowerCase()}.`,
        variant: 'destructive',
      });
    }
  };

  const handleConfirmMarkPaid = async (id: string, paidDate: string) => {
    setIsMarkingPaid(true);
    try {
      const res = await invoicesApi.markAsPaid(id, paidDate);
      if (res.success) {
        toast({ title: 'Status Updated', description: 'Invoice marked as paid.' });
        setPaidDoc(null);
        reload();
        onChanged?.();
      } else {
        toast({
          title: 'Update Failed',
          description: res.errors?.[0] || 'Failed to mark invoice as paid.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsMarkingPaid(false);
    }
  };

  const handleMarkUnpaid = async (id: string) => {
    const res = await invoicesApi.markAsUnpaid(id);
    if (res.success) {
      toast({ title: 'Status Updated', description: 'Invoice marked as unpaid.' });
      reload();
      onChanged?.();
    } else {
      toast({
        title: 'Update Failed',
        description: res.errors?.[0] || 'Failed to mark invoice as unpaid.',
        variant: 'destructive',
      });
    }
  };

  const handleDownloadFile = async (doc: InvoiceRow) => {
    const res = await invoicesApi.downloadFile(doc.id.toString(), doc.original_filename || undefined);
    if (!res.success) {
      toast({
        title: 'Download Failed',
        description: res.error || 'Failed to download attached file.',
        variant: 'destructive',
      });
    }
  };

  const handleDownloadPDF = async (doc: Invoice) => {
    try {
      // PORTAL EDIT (2026-09-17): the file is crm-api's rendered PDF, never one
      // drawn in the browser — see lib/documentPdf.ts.
      await downloadDocumentPdf(doc.id, shareDocumentPdfFilename(documentType, doc.invoice_number));
      toast({ title: 'Success', description: `${noun} PDF downloaded successfully` });
    } catch {
      toast({ title: 'Error', description: 'Failed to download PDF', variant: 'destructive' });
    }
  };

  // A sortable header, pre-bound to this table's sort state. The rendering
  // itself is the SHARED `<SortableHeader>` — the private copy this used to
  // carry is what the shared component was generalised from.
  const SortHeader = ({
    column,
    label,
    align = 'left',
  }: {
    column: string;
    label: string;
    align?: 'left' | 'right';
  }) => (
    <SortableHeader
      column={column}
      label={label}
      sort={sortState.sort}
      dir={sortState.dir}
      onSort={handleSort}
      align={align}
    />
  );

  const colSpan = isQuote ? 6 : 8;

  return (
    <Card {...(className ? { className } : {})} data-testid={`customer-invoices-table-${documentType}`}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon size={20} />
              <Typography component="span" sx={{ fontWeight: 600 }}>
                {noun}s
              </Typography>
              {pagination?.total_count != null && (
                <Chip size="small" variant="outlined" label={pagination.total_count} sx={{ height: 20 }} />
              )}
            </Box>
            <Button size="small" variant="outlined" color="inherit" startIcon={<Plus size={16} />} onClick={goNew}>
              New {noun}
            </Button>
          </Box>
        }
      />
      <CardContent>
        <SearchInput
          placeholder={`Search ${noun.toLowerCase()}s…`}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          searchQuery={searchQuery}
        />

        {error ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'error.main' }}>
            <Typography>{error}</Typography>
            <Button size="small" variant="outlined" color="inherit" onClick={reload} sx={{ mt: 1 }}>
              Retry
            </Button>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={`${noun}s table`}>
              <TableHead>
                <TableRow>
                  <SortHeader column="invoice_number" label={`${noun} #`} />
                  <SortHeader column="issue_date" label="Issue" />
                  <SortHeader column="due_date" label="Due" />
                  <SortHeader column="total_amount" label="Amount" align="right" />
                  {!isQuote && <TableCell align="right">Balance</TableCell>}
                  {!isQuote && <SortHeader column="paid_date" label="Paid" />}
                  {isQuote ? (
                    <TableCell>Status</TableCell>
                  ) : (
                    <SortHeader column="payment_status" label="Status" />
                  )}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {[...Array(colSpan)].map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="rounded" height={16} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center" sx={{ py: 5, color: 'text.disabled' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <Icon size={40} />
                      </Box>
                      <Typography>
                        {searchQuery
                          ? `No ${noun.toLowerCase()}s match “${searchQuery}”`
                          : `No ${noun.toLowerCase()}s yet`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((doc) => {
                    const isRecurring =
                      !isQuote && recurringInvoiceIds.includes(doc.id.toString());
                    const overdue = Boolean(doc.overdue) || doc.payment_status === 'overdue';
                    return (
                      <TableRow key={doc.id} hover>
                        {/* Number */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography
                              component="span"
                              onClick={() => goView(doc)}
                              sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {doc.invoice_number || 'N/A'}
                            </Typography>
                            {doc.has_file && (
                              <Tooltip title="Has attachment">
                                <Box component="span" sx={{ display: 'flex', color: 'text.secondary' }}>
                                  <Paperclip size={14} />
                                </Box>
                              </Tooltip>
                            )}
                            {isRecurring && (
                              <Tooltip title="Recurring billing enabled">
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 0.5,
                                    py: 0.25,
                                    borderRadius: 999,
                                    bgcolor: (t) => alpha(t.palette.secondary.main, 0.12),
                                    color: 'secondary.main',
                                  }}
                                >
                                  <Repeat size={12} />
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        {/* Issue */}
                        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                          {formatDate(doc.issue_date)}
                        </TableCell>
                        {/* Due */}
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            color: overdue ? 'error.main' : 'text.secondary',
                            fontWeight: overdue ? 600 : 400,
                          }}
                        >
                          {formatDate(doc.due_date)}
                        </TableCell>
                        {/* Amount */}
                        <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {formatCurrency(parseAmount(doc.total_amount))}
                        </TableCell>
                        {/* Balance (invoices only) */}
                        {!isQuote && (
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            {doc.payment_status === 'paid'
                              ? formatCurrency(0)
                              : formatCurrency(parseAmount(doc.balance_due ?? doc.total_amount))}
                          </TableCell>
                        )}
                        {/* Paid date (invoices only) */}
                        {!isQuote && (
                          <TableCell
                            sx={{
                              whiteSpace: 'nowrap',
                              color: doc.paid_date ? 'success.main' : 'text.disabled',
                              fontWeight: doc.paid_date ? 500 : 400,
                            }}
                          >
                            {doc.paid_date ? formatDate(doc.paid_date) : '—'}
                          </TableCell>
                        )}
                        {/* Status */}
                        <TableCell>
                          {isQuote ? (
                            (() => {
                              const chip = quoteStatusChip(doc.status);
                              return <Chip size="small" color={chip.color ?? 'default'} label={chip.label} />;
                            })()
                          ) : (
                            <InvoiceStatusBadge status={doc.payment_status || 'unpaid'} overdue={overdue} />
                          )}
                        </TableCell>
                        {/* Actions */}
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            sx={{ width: 32, height: 32 }}
                            aria-label={`${noun} ${doc.invoice_number} actions`}
                            onClick={(e) => setMenuAnchor({ el: e.currentTarget, id: doc.id.toString() })}
                          >
                            <MoreHorizontal size={16} />
                          </IconButton>
                          <Menu
                            anchorEl={menuAnchor?.id === doc.id.toString() ? menuAnchor.el : null}
                            open={menuAnchor?.id === doc.id.toString()}
                            onClose={() => setMenuAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            <ListSubheader sx={{ lineHeight: 2, bgcolor: 'transparent' }}>Actions</ListSubheader>
                            <Divider />
                            <MenuItem onClick={() => { setMenuAnchor(null); goView(doc); }}>
                              <Box sx={{ mr: 1, display: 'flex' }}><Eye size={16} /></Box>
                              View {noun}
                            </MenuItem>
                            <MenuItem onClick={() => { setMenuAnchor(null); goEdit(doc); }}>
                              <Box sx={{ mr: 1, display: 'flex' }}><Edit size={16} /></Box>
                              Edit {noun}
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={() => { setMenuAnchor(null); setShareDoc(doc); }}>
                              <Box sx={{ mr: 1, display: 'flex' }}><Share2 size={16} /></Box>
                              Share
                            </MenuItem>
                            <MenuItem onClick={() => { setMenuAnchor(null); handleDownloadPDF(doc); }}>
                              <Box sx={{ mr: 1, display: 'flex' }}><Download size={16} /></Box>
                              Download PDF
                            </MenuItem>
                            {doc.has_file && (
                              <MenuItem onClick={() => { setMenuAnchor(null); handleDownloadFile(doc); }}>
                                <Box sx={{ mr: 1, display: 'flex' }}><Paperclip size={16} /></Box>
                                Download File
                              </MenuItem>
                            )}
                            {!isQuote && (
                              <>
                                <Divider />
                                <ListSubheader sx={{ lineHeight: 2, bgcolor: 'transparent' }}>Status</ListSubheader>
                                {doc.payment_status !== 'paid' ? (
                                  <MenuItem onClick={() => { setMenuAnchor(null); setPaidDoc(doc); }}>
                                    <Box sx={{ mr: 1, display: 'flex', color: 'success.main' }}><Check size={16} /></Box>
                                    Mark as Paid
                                  </MenuItem>
                                ) : (
                                  <MenuItem onClick={() => { setMenuAnchor(null); handleMarkUnpaid(doc.id.toString()); }}>
                                    <Box sx={{ mr: 1, display: 'flex', color: 'text.secondary' }}><Clock size={16} /></Box>
                                    Mark as Unpaid
                                  </MenuItem>
                                )}
                              </>
                            )}
                            <Divider />
                            <MenuItem
                              onClick={() => { setMenuAnchor(null); setDeleteId(doc.id.toString()); }}
                              sx={{ color: 'error.main' }}
                            >
                              <Box sx={{ mr: 1, display: 'flex' }}><CloseIcon size={16} /></Box>
                              Delete {noun}
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Server-side pagination (25/page default) through the shared primitive. */}
        {!error && (
          <Pagination
            page={page}
            perPage={perPage}
            totalCount={pagination?.total_count ?? 0}
            totalPages={pagination?.total_pages ?? 1}
            onPageChange={setPage}
            onPerPageChange={(pp) => {
              setPerPage(pp);
              setPage(1);
            }}
            perPageOptions={PER_PAGE_OPTIONS}
            entityNoun={`${noun.toLowerCase()}s`}
            hideIfSinglePage
          />
        )}
      </CardContent>

      {/* Modals */}
      <DeleteInvoiceModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        invoiceId={deleteId}
        onConfirmDelete={handleConfirmDelete}
        documentType={documentType}
      />
      {!isQuote && (
        <MarkAsPaidModal
          isOpen={paidDoc !== null}
          onClose={() => setPaidDoc(null)}
          invoice={paidDoc}
          onConfirm={handleConfirmMarkPaid}
          isLoading={isMarkingPaid}
        />
      )}
      <ShareDocumentModal
        isOpen={shareDoc !== null}
        onClose={() => setShareDoc(null)}
        documentId={shareDoc?.id?.toString() || null}
        documentType={documentType}
        {...(shareDoc?.invoice_number ? { documentNumber: shareDoc.invoice_number } : {})}
      />
    </Card>
  );
}
